import type {
	IFireInsuranceQuotation,
	IFireInsuranceQuoteData,
} from '@/interfaces/IFireInsuranceQuotation';
import type { IResponseBase } from '@/interfaces';
import type {
	IFireInsuranceReissueRequest,
	IFireInsuranceReissueResult,
} from '@/interfaces/IFireInsuranceReissue';
import type { CompanyType, PaymentMethod } from '@/constants';
import { Http } from './http';

const http = Http.create();

/**
 * Self-service de Seguro Incêndio. A criação do caso usa o endpoint genérico
 * (CaseService.addCase com caseType = CASE_FIRE_INSURANCE), que dispara o fan-out
 * das comissões. Aqui ficam o comparativo e a seleção da comissão.
 */
export class FireInsuranceService {
	/** Comparativo das 4 comissões (0/10/20/30) do caso. */
	static async getComparison(caseId: string): Promise<IFireInsuranceQuotation[]> {
		const response = await http.get<IResponseBase<IFireInsuranceQuotation[]>>(
			`/fire-insurance/self-service/${caseId}/quotations`,
		);
		return response.data.data;
	}

	/**
	 * Seleciona a comissão escolhida, salva a forma de pagamento (Faturado ou
	 * Multi pagamentos) na Quotation e dispara a emissão da proposta.
	 */
	static async selectCommission(
		caseId: string,
		commissionPct: number,
		paymentMethod: PaymentMethod,
		installments?: number,
	): Promise<void> {
		await http.post(`/fire-insurance/self-service/${caseId}/select-commission`, {
			commissionPct,
			paymentMethod,
			installments,
		});
	}

	/** Re-cota uma única comissão que falhou na seguradora (retry por comissão). */
	static async requoteCommission(caseId: string, commissionPct: number): Promise<void> {
		await http.post(`/fire-insurance/self-service/${caseId}/quotations/${commissionPct}/requote`);
	}

	/**
	 * Redispara o fan-out inteiro. É a saída do caso que ficou sem cotação nenhuma (falha que
	 * escapou, timeout da seguradora, dado incompleto) e do caso recotado após corrigir os dados.
	 */
	static async requoteAll(caseId: string): Promise<void> {
		await http.post(`/fire-insurance/self-service/${caseId}/requote`);
	}

	/**
	 * Corrige os dados que alimentam a cotação. Invalida as cotações atuais e NÃO recota — quem
	 * dispara é o `requoteAll`, por escolha explícita do operador.
	 */
	static async updateQuoteData(caseId: string, payload: IFireInsuranceQuoteData): Promise<void> {
		await http.patch(`/fire-insurance/self-service/${caseId}/quote-data`, payload);
	}

	/**
	 * Marca a atividade de pagamento como concluída (reconciliação manual do backoffice).
	 * Não emite a proposta — a emissão segue acontecendo no checkout do cliente.
	 */
	static async markPaymentPaid(caseId: string): Promise<void> {
		await http.post(`/fire-insurance/self-service/${caseId}/payment/mark-paid`);
	}

	// ---- Cotação de incêndio dentro do fluxo da Garantia (step da seleção de produtos) ----
	// As cotações ficam vinculadas ao id da GARANTIA; o caso de incêndio só é criado ao
	// finalizar a garantia (UpdateCaseWithProduct com a comissão/pagamento escolhidos).

	/**
	 * Roda a cotação do incêndio para uma garantia (persiste locador) e retorna o comparativo.
	 *
	 * `renterCompanyType`/`renterEconomicActivityCode` só vão quando o locatário é PJ: o backend
	 * os grava no RenterA da garantia (só o incêndio usa esses campos) e, quando eles mudam,
	 * refaz o fan-out inteiro — as cotações anteriores saíram como PF e não seriam comparáveis.
	 */
	static async quoteForGuarantee(
		guaranteeCaseId: string,
		locator: {
			locatorName: string;
			locatorDocument: string;
			renterCompanyType?: CompanyType | null;
			renterEconomicActivityCode?: number | null;
		},
		force = false,
	): Promise<IFireInsuranceQuotation[]> {
		const response = await http.post<IResponseBase<IFireInsuranceQuotation[]>>(
			`/fire-insurance/self-service/guarantee/${guaranteeCaseId}/quote`,
			{ ...locator, force },
		);
		return response.data.data;
	}

	/** Comparativo das comissões de incêndio vinculadas a uma garantia. */
	static async getGuaranteeComparison(guaranteeCaseId: string): Promise<IFireInsuranceQuotation[]> {
		const response = await http.get<IResponseBase<IFireInsuranceQuotation[]>>(
			`/fire-insurance/self-service/guarantee/${guaranteeCaseId}/quotations`,
		);
		return response.data.data;
	}

	/** Re-cota uma comissão específica de incêndio de uma garantia (retry por comissão). */
	static async requoteForGuarantee(guaranteeCaseId: string, commissionPct: number): Promise<void> {
		await http.post(
			`/fire-insurance/self-service/guarantee/${guaranteeCaseId}/quotations/${commissionPct}/requote`,
		);
	}

	/**
	 * Registra uma apólice reemitida à mão no portal da Porto, devolvendo o caso cancelado à
	 * esteira (ENG-597).
	 *
	 * Vai o número da PROPOSTA, não o da apólice: é por ele que o worker acha o caso, e é dele que
	 * a Porto deriva o número da apólice, o da renovação e os dois PDFs.
	 */
	static async reissue(
		caseId: string,
		payload: IFireInsuranceReissueRequest,
	): Promise<IFireInsuranceReissueResult> {
		const response = await http.post<IResponseBase<IFireInsuranceReissueResult>>(
			`/FireInsurance/${caseId}/reissue`,
			payload,
		);

		return response.data.data;
	}
}
